import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { Course, Exam, Entitlement, ExamAttempt } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { AlertCircle, CheckCircle, Clock, AlertTriangle, Wifi, Maximize, Ban, ShieldCheck, FileQuestion } from 'lucide-react'

export function ExamStartPage() {
  useDocumentTitle('試験開始')
  const { examId } = useParams<{ examId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exam, setExam] = useState<Exam | null>(null)
  const [course, setCourse] = useState<Course | null>(null)
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null)
  const [eligibilityCheck, setEligibilityCheck] = useState({
    hasEntitlement: false,
    allLessonsCompleted: false,
    withinPeriod: false,
    hasInProgressAttempt: false,
  })
  const [starting, setStarting] = useState(false)
  const [inProgressAttempt, setInProgressAttempt] = useState<ExamAttempt | null>(null)

  useEffect(() => {
    if (!user || !examId) return
    checkExamEligibility()
  }, [examId, user])

  const checkExamEligibility = async () => {
    if (!examId || !user) return

    try {
      setLoading(true)
      setError(null)

      // 試験情報を取得
      const { data: examData, error: examError } = await supabase
        .from('exams')
        .select('*, courses(*)')
        .eq('exam_id', examId)
        .single()

      if (examError || !examData) {
        setError('試験が見つかりませんでした')
        setLoading(false)
        return
      }

      setExam(examData)
      const courseData = examData.courses as any
      setCourse(courseData)

      // 受講権を確認
      const { data: entitlementData } = await supabase
        .from('entitlements')
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', courseData.course_id)
        .eq('status', 'active')
        .single()

      const hasEntitlement = !!entitlementData
      setEntitlement(entitlementData)

      // 受講期間チェック
      const now = new Date()
      const withinPeriod = hasEntitlement
        ? (!entitlementData.valid_from || new Date(entitlementData.valid_from) <= now) &&
          (!entitlementData.valid_until || new Date(entitlementData.valid_until) >= now)
        : false

      // 全レッスン完了チェック
      let allLessonsCompleted = false
      if (hasEntitlement) {
        const { count: totalLessons } = await supabase
          .from('course_lessons')
          .select('*', { count: 'exact', head: true })
          .eq('course_id', courseData.course_id)

        const { count: completedLessons } = await supabase
          .from('lesson_progress')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('course_id', courseData.course_id)
          .eq('status', 'completed')

        allLessonsCompleted = totalLessons !== null && totalLessons === completedLessons && totalLessons > 0
      }

      // 進行中の試験があるかチェック
      const { data: inProgressData } = await supabase
        .from('exam_attempts')
        .select('*')
        .eq('user_id', user.id)
        .eq('exam_id', examId)
        .eq('status', 'in_progress')
        .single()

      setInProgressAttempt(inProgressData)

      setEligibilityCheck({
        hasEntitlement,
        allLessonsCompleted,
        withinPeriod,
        hasInProgressAttempt: !!inProgressData,
      })
    } catch (err: any) {
      console.error('Error checking exam eligibility:', err)
      setError(err.message || '受験資格の確認に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleStartExam = async () => {
    if (!exam || !user) return

    try {
      setStarting(true)
      setError(null)

      const bffUrl = import.meta.env.VITE_BFF_URL
      const token = (await supabase.auth.getSession()).data.session?.access_token

      if (!token) {
        throw new Error('認証トークンが取得できませんでした')
      }

      const response = await fetch(`${bffUrl}/exam-start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          exam_id: exam.exam_id,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || '試験の開始に失敗しました')
      }

      const data = await response.json()

      // 試験受験ページへ遷移
      navigate(`/exams/attempt/${data.attempt_id}`)
    } catch (err: any) {
      console.error('Error starting exam:', err)
      setError(err.message || '試験の開始に失敗しました')
      setStarting(false)
    }
  }

  const handleResumeExam = () => {
    if (inProgressAttempt) {
      navigate(`/exams/attempt/${inProgressAttempt.attempt_id}`)
    }
  }

  const canStartExam =
    eligibilityCheck.hasEntitlement &&
    eligibilityCheck.allLessonsCompleted &&
    eligibilityCheck.withinPeriod &&
    !eligibilityCheck.hasInProgressAttempt

  if (loading) {
    return (
      <MainLayout>
        <Loading message="受験資格を確認中..." />
      </MainLayout>
    )
  }

  if (!exam || !course) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error || '試験が見つかりませんでした'} />
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f9fafb]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{exam.title}</h1>
          <p className="text-gray-500 mb-8">講座: {course.title}</p>

          {/* 試験情報 */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">試験情報</h2>

            {/* Key Stats Row */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1 bg-gray-50/80 rounded-xl p-4 flex flex-col items-center justify-center border border-gray-100">
                <Clock className="w-6 h-6 text-[#006cc6] mb-2" />
                <span className="text-2xl font-bold text-gray-900 tracking-tight">
                  {exam.time_limit_minutes}
                </span>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  分
                </span>
              </div>
              <div className="flex-1 bg-gray-50/80 rounded-xl p-4 flex flex-col items-center justify-center border border-gray-100">
                <FileQuestion className="w-6 h-6 text-[#006cc6] mb-2" />
                <span className="text-2xl font-bold text-gray-900 tracking-tight">
                  {exam.passing_score}
                </span>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  点
                </span>
              </div>
            </div>

            {exam.description && (
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">{exam.description}</p>
              </div>
            )}
          </div>

        {/* 受験資格チェック */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">受験資格</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50/50">
              {eligibilityCheck.hasEntitlement ? (
                <div className="p-1 rounded-full bg-green-100">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
              ) : (
                <div className="p-1 rounded-full bg-red-100">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                </div>
              )}
              <div className="flex-1">
                <p
                  className={`text-sm font-semibold ${eligibilityCheck.hasEntitlement ? 'text-green-800' : 'text-red-800'}`}
                >
                  講座の受講権
                </p>
                {!eligibilityCheck.hasEntitlement && (
                  <p className="text-xs text-gray-600 mt-0.5">この講座の受講権が必要です</p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50/50">
              {eligibilityCheck.allLessonsCompleted ? (
                <div className="p-1 rounded-full bg-green-100">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
              ) : (
                <div className="p-1 rounded-full bg-red-100">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                </div>
              )}
              <div className="flex-1">
                <p
                  className={`text-sm font-semibold ${eligibilityCheck.allLessonsCompleted ? 'text-green-800' : 'text-red-800'}`}
                >
                  全レッスンの完了
                </p>
                {!eligibilityCheck.allLessonsCompleted && (
                  <p className="text-xs text-gray-600 mt-0.5">
                    全てのレッスンを完了してください
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50/50">
              {eligibilityCheck.withinPeriod ? (
                <div className="p-1 rounded-full bg-green-100">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
              ) : (
                <div className="p-1 rounded-full bg-red-100">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                </div>
              )}
              <div className="flex-1">
                <p
                  className={`text-sm font-semibold ${eligibilityCheck.withinPeriod ? 'text-green-800' : 'text-red-800'}`}
                >
                  受講期間内
                </p>
                {!eligibilityCheck.withinPeriod && entitlement && (
                  <p className="text-xs text-gray-600 mt-0.5">
                    受講期間:{' '}
                    {entitlement.valid_from &&
                      new Date(entitlement.valid_from).toLocaleDateString('ja-JP')}{' '}
                    〜{' '}
                    {entitlement.valid_until &&
                      new Date(entitlement.valid_until).toLocaleDateString('ja-JP')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 進行中の試験がある場合 */}
        {eligibilityCheck.hasInProgressAttempt && inProgressAttempt && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-yellow-100">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                  進行中の試験があります
                </h3>
                <p className="text-sm text-yellow-800 mb-3">
                  前回の試験が終了していません。続きから受験してください。
                </p>
                <p className="text-sm text-yellow-700 mb-4">
                  制限時間:{' '}
                  {new Date(inProgressAttempt.ends_at).toLocaleString('ja-JP')} まで
                </p>
                <button
                  onClick={handleResumeExam}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium shadow-sm"
                >
                  試験を再開する
                </button>
              </div>
            </div>
          </div>
        )}

        {/* エラー表示 */}
        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}

        {/* アクションボタン */}
        <div className="space-y-3">
          <button
            onClick={handleStartExam}
            disabled={!canStartExam || starting}
            className="w-full py-3.5 px-4 bg-[#006cc6] hover:bg-[#06579e] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#006cc6] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors shadow-sm shadow-blue-500/20"
          >
            {starting ? '試験を開始中...' : '試験を開始する'}
          </button>

          <button
            onClick={() => navigate(`/courses/${course.course_id}`)}
            className="w-full py-2.5 px-4 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            講座詳細へ戻る
          </button>
        </div>

        {/* 注意事項 */}
        {canStartExam && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-4">
              受験前の注意事項
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4 group">
                <div className="p-2 rounded-lg bg-blue-100 text-[#006cc6] group-hover:bg-[#006cc6] group-hover:text-white transition-colors">
                  <Wifi size={18} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">安定したネットワーク接続</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    信頼できるネットワークに接続されていることを確認してください
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 group">
                <div className="p-2 rounded-lg bg-blue-100 text-[#006cc6] group-hover:bg-[#006cc6] group-hover:text-white transition-colors">
                  <Maximize size={18} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">全画面表示を推奨</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    ブラウザを最大化することをお勧めします
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 group">
                <div className="p-2 rounded-lg bg-blue-100 text-[#006cc6] group-hover:bg-[#006cc6] group-hover:text-white transition-colors">
                  <Ban size={18} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">ページの更新禁止</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    ページを更新すると進捗が失われる可能性があります
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 group">
                <div className="p-2 rounded-lg bg-blue-100 text-[#006cc6] group-hover:bg-[#006cc6] group-hover:text-white transition-colors">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">自動保存機能</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    回答は自動的に保存されます
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </MainLayout>
  )
}
